import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react'
import { useThrottleChanges } from './useThrottleChanges'
import { useInterval } from './useInterval'
import { useAevocamAssetState } from './useAevocamAssetState'
import {
  clampPanToKeepViewerVisible,
  computeChromeViewerWidth,
  fitZoomLevelForViewportOrUndefined,
  photoDisplayAspectRatio,
  viewerFitsInsideViewport,
} from './layoutZoomMath'
import type { Feed, Photo, SpriteSheet } from './AevocamViewer'
import { AevocamPlayerShell } from './AevocamPlayerShell'
import {
  findPhotoIndexAfterTimeSlotChange,
  photoToAnchor,
  type FilterChangeAnchors,
  type PhotoAnchor,
} from './photoAnchor'

//--------------------------------------------------------------------------------
/** Payload for {@link AevocamPlayerProviderProps.onFeedSummary} when the REST feed summary has loaded. */
export type AevocamFeedSummary = {
  feed: Feed
  totalPhotos: number
}

//--------------------------------------------------------------------------------
export type AevocamPlayerProviderProps = {
  startAtLatest: boolean
  sessionKey?: string
  cdnUrl: string
  apiUrl: string
  tenantId: string
  feedId: string
  /** When true, keyboard shortcuts listen on `document.body` instead of the viewport */
  keyboardHandlersGlobal?: boolean
  /** Fired when timed playback hits an end: first photo (reverse) or last photo (forward). */
  onPlaybackEnd?: (firstPhoto: boolean) => void
  /** Fired once when the feed summary API returns (before manifest). */
  onFeedSummary?: (summary: AevocamFeedSummary) => void
  children: ReactNode
}

//--------------------------------------------------------------------------------
export function AevocamPlayerProvider({ children, ...config }: AevocamPlayerProviderProps) {
  const value = useAevocamPlayerController(config)
  return createElement(
    AevocamPlayerContext.Provider,
    { value },
    createElement(AevocamPlayerShell, { shellRef: value.playerShellRef, children }),
  )
}

//--------------------------------------------------------------------------------
export function useAevocamPlayer(): AevocamPlayerContextValue {
  const ctx = useContext(AevocamPlayerContext)
  if (!ctx) {
    throw new Error('useAevocamPlayer must be used within an AevocamPlayerProvider')
  }
  return ctx
}

//--------------------------------------------------------------------------------
export type AevocamPlayerProviderConfig = Omit<AevocamPlayerProviderProps, 'children'>

//--------------------------------------------------------------------------------
function useAevocamPlayerController(config: AevocamPlayerProviderConfig): AevocamPlayerContextValue {
  const {
    startAtLatest,
    sessionKey,
    cdnUrl,
    apiUrl,
    tenantId,
    feedId,
    keyboardHandlersGlobal = false,
    onPlaybackEnd,
    onFeedSummary,
  } = config

  //......................................................................
  const onPlaybackEndRef = useRef(onPlaybackEnd)
  useEffect(() => {
    onPlaybackEndRef.current = onPlaybackEnd
  }, [onPlaybackEnd])

  const onFeedSummaryRef = useRef(onFeedSummary)
  useEffect(() => {
    onFeedSummaryRef.current = onFeedSummary
  }, [onFeedSummary])

  //......................................................................
  const [playback, setPlayback] = useState<PlaybackState>(() => ({
    currentIndex: 0,
    isPlaying: false,
    playReverse: startAtLatest,
    frameRate: 10,
    frameRateOptions: [...DEFAULT_FRAME_RATE_OPTIONS],
    playbackRepeat: 'single',
    inFrame: -1,
    outFrame: -1,
    stayBetweenInOut: true,
  }))

  //......................................................................
  const [layout, setLayout] = useState<LayoutState>(() => ({
    rawContainerWidth: undefined,
    externalWidth: undefined,
    zoomLevel: 1.0,
    zoomFit: true,
    panX: 0,
    panY: 0,
    proactiveLoading: true,
    viewportSize: { width: 0, height: 0 },
  }))

  //......................................................................
  const [feedState, setFeedState] = useState<FeedState>(() => ({
    loadingStage: 0,
    totalPhotos: 0,
    feed: null,
    currentPhoto: undefined,
  }))

  //......................................................................
  const [filters, setFilters] = useState<FilterState>(() => ({
    timeSlot: undefined,
    markOutliers: false,
  }))
  const [isFullScreen, setFullScreen] = useState(false)

  //......................................................................
  const lastActivityMsRef = useRef(Date.now())
  const containerRef = useRef<HTMLDivElement>(null)
  const skipNextViewportClickRef = useRef(false)
  /** Viewport mousedown while focus was outside the shell; next click skips play/pause once. */
  const deferPlayPauseRef = useRef(false)
  const viewportRef = useRef<HTMLDivElement>(null)
  const playerShellRef = useRef<HTMLDivElement>(null)
  const playbackIntervalRef = useRef<number | null>(null)
  const viewportSizeRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 })
  const previousZoomLevelRef = useRef(1.0)
  /** Playback markers to restore after changing the hour filter (cleared once applied). */
  const filterChangeRestoreRef = useRef<FilterChangeAnchors | null>(null)

  //......................................................................
  const containerWidth = useThrottleChanges(layout.rawContainerWidth, 250)

  //......................................................................
  const {
    currentIndex,
    isPlaying,
    playReverse,
    frameRate,
    frameRateOptions,
    playbackRepeat,
    inFrame,
    outFrame,
    stayBetweenInOut,
  } = playback

  //......................................................................
  const {
    rawContainerWidth,
    externalWidth,
    zoomLevel,
    zoomFit,
    panX,
    panY,
    proactiveLoading,
    viewportSize,
  } = layout

  //......................................................................
  const {
    loadingStage,
    totalPhotos,
    feed,
    currentPhoto,
  } = feedState

  //......................................................................
  useEffect(() => {
    lastActivityMsRef.current = Date.now()
    // Avoid a layout state write on every playback frame; redundant updates can
    // re-render the tree between mousedown and mouseup and cancel toolbar clicks.
    setLayout(l => (l.proactiveLoading ? l : { ...l, proactiveLoading: true }))
  }, [currentIndex])

  //......................................................................
  useInterval(() => {
    if (Date.now() - lastActivityMsRef.current >= INACTIVITY_MS) {
      setLayout(l => ({ ...l, proactiveLoading: false }))
    }
  }, 1000)

  //......................................................................
  const onSummaryLoaded = useCallback(
    (nextFeed: Feed, nextTotal: number) => {
      setFeedState(f => ({
        ...f,
        feed: nextFeed,
        totalPhotos: nextTotal,
        loadingStage: 1,
      }))
      if (!filterChangeRestoreRef.current) {
        setPlayback(p => ({
          ...p,
          currentIndex: startAtLatest && nextTotal > 0 ? nextTotal - 1 : 0,
        }))
      }
      onFeedSummaryRef.current?.({ feed: nextFeed, totalPhotos: nextTotal })
    },
    [startAtLatest],
  )

  //......................................................................
  const onManifestLoaded = useCallback(() => {
    setFeedState(f => ({ ...f, loadingStage: 2 }))
  }, [])

  //......................................................................
  const onCurrentPhoto = useCallback((photo: Photo | undefined) => {
    setFeedState(f => ({ ...f, currentPhoto: photo }))
  }, [])

  //......................................................................
  const aspectRatio = photoDisplayAspectRatio(currentPhoto)

  //......................................................................
  const { viewerWidth, calculatedFitZoom } = useMemo(
    () =>
      computeChromeViewerWidth({
        zoomFit,
        currentPhoto,
        viewportSize,
        zoomLevel,
        externalWidth,
        containerWidth,
      }),
    [zoomFit, currentPhoto, zoomLevel, externalWidth, containerWidth, viewportSize],
  )

  //......................................................................
  const {
    pipelineStage: viewerPipelineStage,
    photos: viewerPhotos,
    spriteSheetsLow,
    spriteSheetsMedium,
    spritesHighGearLow,
    spritesHighGearMedium,
    viewWidth: viewerViewWidth,
    viewHeight: viewerViewHeight,
    cdnToken,
    cdnTokenTtlSec,
    setSpritesHighGearLow,
    setSpritesHighGearMedium,
  } = useAevocamAssetState({
    startAtLatest,
    sessionKey,
    cdnUrl,
    apiUrl,
    tenantId,
    feedId,
    timeSlot: filters.timeSlot,
    markOutliers: filters.markOutliers,
    layoutViewerWidth: viewerWidth,
    currentIndex,
    reportSummaryLoaded: onSummaryLoaded,
    reportManifestLoaded: onManifestLoaded,
    reportCurrentPhoto: onCurrentPhoto,
  })

  //......................................................................
  // Keep chrome controls aligned with the manifest result length (esp. when filtered).
  useEffect(() => {
    if (viewerPipelineStage < 2) return
    const nextTotal = viewerPhotos.length
    setFeedState(f => (f.totalPhotos === nextTotal
      ? f
      : {
        ...f,
        totalPhotos: nextTotal,
      })
    )
    if (!filterChangeRestoreRef.current) {
      setPlayback(p => {
        const cap = Math.max(nextTotal - 1, 0)
        const nextIndex = Math.min(p.currentIndex, cap)
        if (nextIndex === p.currentIndex) return p
        return {
          ...p,
          currentIndex: nextIndex,
          isPlaying: false,
        }
      })
    }
  }, [viewerPipelineStage, viewerPhotos.length])

  //......................................................................
  // After an hour-filter change, restore current / in / out by photo identity.
  useEffect(() => {
    if (viewerPipelineStage < 2 || viewerPhotos.length === 0) return
    const anchors = filterChangeRestoreRef.current
    if (!anchors) return
    filterChangeRestoreRef.current = null

    const cap = Math.max(viewerPhotos.length - 1, 0)
    const clampIdx = (idx: number) => Math.min(Math.max(idx, 0), cap)
    const remap = (anchor: PhotoAnchor | null) =>
      anchor ? clampIdx(findPhotoIndexAfterTimeSlotChange(viewerPhotos, anchor)) : undefined

    setPlayback(p => {
      const currentIndex = anchors.current
        ? remap(anchors.current)!
        : clampIdx(p.currentIndex)
      const inFrame = anchors.in
        ? remap(anchors.in)!
        : (p.inFrame >= 0 ? clampIdx(p.inFrame) : -1)
      const outFrame = anchors.out
        ? remap(anchors.out)!
        : (p.outFrame >= 0 ? clampIdx(p.outFrame) : -1)
      return { ...p, currentIndex, inFrame, outFrame }
    })
  }, [viewerPipelineStage, viewerPhotos])

  //......................................................................
  useEffect(() => {
    if (!isPlaying) {
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current)
        playbackIntervalRef.current = null
      }
      return
    }

    const intervalMs = 1000 / frameRate
    playbackIntervalRef.current = window.setInterval(() => {
      setPlayback(p => {
        const { lo, hi } = playbackIndexBounds(
          p.stayBetweenInOut,
          p.inFrame,
          p.outFrame,
          totalPhotos,
        )
        if (p.playReverse) {
          const next = p.currentIndex - 1
          if (next < lo) {
            queueMicrotask(() => onPlaybackEndRef.current?.(true))
            if (p.playbackRepeat === 'oscillate') {
              return { ...p, playReverse: false, currentIndex: lo + 1, isPlaying: true }
            }
            if (p.playbackRepeat === 'loop') {
              return { ...p, currentIndex: hi, isPlaying: true }
            }
            return { ...p, currentIndex: lo, isPlaying: false }
          }
          return { ...p, currentIndex: next }
        }
        const next = p.currentIndex + 1
        if (next > hi) {
          queueMicrotask(() => onPlaybackEndRef.current?.(false))
          if (p.playbackRepeat === 'oscillate') {
            return { ...p, playReverse: true, currentIndex: hi - 1, isPlaying: true }
          }
          if (p.playbackRepeat === 'loop') {
            return { ...p, currentIndex: lo, isPlaying: true }
          }
          return {
            ...p,
            currentIndex: hi,
            isPlaying: false,
          }
        }
        return { ...p, currentIndex: next }
      })
    }, intervalMs)

    return () => {
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current)
        playbackIntervalRef.current = null
      }
    }
  }, [isPlaying, playReverse, frameRate, totalPhotos])

  //......................................................................
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const w = entry.contentRect.width
        if (w > 0) {
          setLayout(l => ({ ...l, rawContainerWidth: w }))
        }
      }
    })
    resizeObserver.observe(container)
    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  //......................................................................
  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const newSize = {
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        }
        viewportSizeRef.current = newSize
        setLayout(l => {
          let next = { ...l, viewportSize: newSize }
          if (l.zoomFit && currentPhoto) {
            const fitZoom = fitZoomLevelForViewportOrUndefined(
              currentPhoto,
              entry.contentRect.width,
              entry.contentRect.height,
            )
            if (fitZoom !== undefined) {
              next = { ...next, zoomLevel: fitZoom }
            }
          }
          return next
        })
      }
    })
    resizeObserver.observe(viewport)
    return () => {
      resizeObserver.disconnect()
    }
  }, [zoomFit, currentPhoto])

  //......................................................................
  useEffect(() => {
    if (zoomFit && calculatedFitZoom !== undefined) {
      setLayout(l => ({ ...l, zoomLevel: calculatedFitZoom }))
    }
  }, [zoomFit, calculatedFitZoom])

  //......................................................................
  const viewerFitsInViewport = useCallback((): boolean => {
    if (!currentPhoto) return false
    return viewerFitsInsideViewport(currentPhoto, zoomFit, zoomLevel, viewportSizeRef.current)
  }, [currentPhoto, zoomFit, zoomLevel])

  //......................................................................
  useEffect(() => {
    if (previousZoomLevelRef.current !== zoomLevel && previousZoomLevelRef.current > 0) {
      const zoomRatio = zoomLevel / previousZoomLevelRef.current
      setLayout(l => ({
        ...l,
        panX: l.panX * zoomRatio,
        panY: l.panY * zoomRatio,
      }))
    }
    previousZoomLevelRef.current = zoomLevel
    // Only force centering in fit mode. When zooming freely (`zoomFit` false), the viewer can
    // become smaller than the viewport; resetting pan here broke shift+wheel zoom-to-pointer.
    if (zoomFit && viewerFitsInViewport()) {
      setLayout(l => ({ ...l, panX: 0, panY: 0 }))
    }
  }, [zoomLevel, zoomFit, currentPhoto, viewerFitsInViewport])

  //......................................................................
  const setDirection = useCallback((reverse: boolean) => {
    setPlayback(p => ({ ...p, playReverse: reverse }))
  }, [])

  //......................................................................
  const setPlaybackRepeat = useCallback((mode: PlaybackRepeatMode) => {
    setPlayback(p => ({ ...p, playbackRepeat: mode }))
  }, [])

  //......................................................................
  const setInFrame = useCallback((index: number = -1) => {
    setPlayback(p => {
      if (index < 0) {
        return { ...p, inFrame: -1 }
      }
      let inFrame = index
      let outFrame = p.outFrame
      if (outFrame !== -1 && inFrame >= outFrame) {
        outFrame = -1
      }
      return { ...p, inFrame, outFrame }
    })
  }, [])

  //......................................................................
  const setOutFrame = useCallback((index: number = -1) => {
    setPlayback(p => {
      if (index < 0) {
        return { ...p, outFrame: -1 }
      }
      let inFrame = p.inFrame
      let outFrame = index
      if (inFrame !== -1 && outFrame <= inFrame) {
        inFrame = -1
      }
      return { ...p, inFrame, outFrame }
    })
  }, [])

  //......................................................................
  const setStayBetweenInOut = useCallback((value: boolean) => {
    setPlayback(p => ({ ...p, stayBetweenInOut: value }))
  }, [])

  //......................................................................
  /**
   * Starts timed playback. When `autoReverse` is true, flips `playReverse` if the current
   * index is already at the bound in the active direction (respecting stay-between-in/out).
   */
  const play = useCallback((autoReverse = false) => {
    setPlayback(p => {
      const { lo, hi } = playbackIndexBounds(
        p.stayBetweenInOut,
        p.inFrame,
        p.outFrame,
        totalPhotos,
      )
      const idx = Math.min(Math.max(p.currentIndex, lo), hi)

      let reverse = p.playReverse
      if (autoReverse && p.playbackRepeat !== 'loop') {
        if (!reverse && idx >= hi) {
          reverse = true
        } else if (reverse && idx <= lo) {
          reverse = false
        }
      }

      return { ...p, playReverse: reverse, currentIndex: idx, isPlaying: true }
    })
  }, [totalPhotos])

  //......................................................................
  const stop = useCallback(() => {
    setPlayback(p => ({ ...p, isPlaying: false }))
  }, [])

  //......................................................................
  const playPause = useCallback((reverse: boolean) => {
    setPlayback(p => {
      if (p.isPlaying && p.playReverse === reverse) {
        return { ...p, isPlaying: false }
      }
      const { lo, hi } = playbackIndexBounds(
        p.stayBetweenInOut,
        p.inFrame,
        p.outFrame,
        totalPhotos,
      )
      const idx = Math.min(Math.max(p.currentIndex, lo), hi)
      return { ...p, playReverse: reverse, currentIndex: idx, isPlaying: true }
    })
  }, [totalPhotos])

  //......................................................................
  const goFirst = useCallback((limitToInOut = false) => {
    setPlayback(p => {
      const { lo } = playbackIndexBounds(limitToInOut, p.inFrame, p.outFrame, totalPhotos)
      return { ...p, currentIndex: lo, isPlaying: false }
    })
  }, [totalPhotos])

  //......................................................................
  const goLast = useCallback((limitToInOut = false) => {
    setPlayback(p => {
      const { hi } = playbackIndexBounds(limitToInOut, p.inFrame, p.outFrame, totalPhotos)
      return { ...p, currentIndex: hi, isPlaying: false }
    })
  }, [totalPhotos])

  //......................................................................
  const advance = useCallback((frames: number, limitToInOut = false) => {
    setPlayback(p => {
      const { lo, hi } = playbackIndexBounds(limitToInOut, p.inFrame, p.outFrame, totalPhotos)
      const next = p.currentIndex + frames
      return {
        ...p,
        currentIndex: Math.min(Math.max(next, lo), hi),
      }
    })
  }, [totalPhotos])

  //......................................................................
  const bumpFrameRateUp = useCallback(() => {
    setPlayback(p => {
      const i = p.frameRateOptions.indexOf(p.frameRate)
      if (i >= 0 && i < p.frameRateOptions.length - 1) {
        return { ...p, frameRate: p.frameRateOptions[i + 1] ?? p.frameRate }
      }
      return p
    })
  }, [])

  //......................................................................
  const bumpFrameRateDown = useCallback(() => {
    setPlayback(p => {
      const i = p.frameRateOptions.indexOf(p.frameRate)
      if (i > 0) {
        return { ...p, frameRate: p.frameRateOptions[i - 1] ?? p.frameRate }
      }
      return p
    })
  }, [])

  //......................................................................
  /** Shift+wheel zoom ratio (tuned softer than button +/- steps). */
  const zoomStepTowardViewportPoint = useCallback(
    (deltaY: number, viewportX: number, viewportY: number) => {
      if (deltaY === 0) return
      setLayout(l => {
        const { width: vw, height: vh } = l.viewportSize
        if (vw <= 0 || vh <= 0) return l
        const z0 = l.zoomLevel
        if (z0 <= 0) return l
        const z1 =
          deltaY < 0
            ? Math.min(ZOOM_STEP_RATIO_MAX, z0 / ZOOM_SHIFT_WHEEL_STEP)
            : Math.max(ZOOM_STEP_RATIO_MIN, z0 * ZOOM_SHIFT_WHEEL_STEP)
        const r = z1 / z0
        if (Math.abs(r - 1) < 1e-12) {
          return { ...l, zoomFit: false, zoomLevel: z1 }
        }
        const panFx = (1 - r) * (viewportX - vw / 2) + r * l.panX
        const panFy = (1 - r) * (viewportY - vh / 2) + r * l.panY
        return {
          ...l,
          zoomFit: false,
          zoomLevel: z1,
          panX: panFx / r,
          panY: panFy / r,
        }
      })
    },
    [],
  )

  //......................................................................
  /** Pinch: scaleRatio = current finger separation / previous frame's separation (same focal-point math as wheel zoom). */
  const pinchZoomAtViewportPoint = useCallback(
    (scaleRatio: number, viewportX: number, viewportY: number) => {
      if (!(scaleRatio > 0) || !Number.isFinite(scaleRatio)) return
      setLayout(l => {
        const { width: vw, height: vh } = l.viewportSize
        if (vw <= 0 || vh <= 0) return l
        const z0 = l.zoomLevel
        if (z0 <= 0) return l
        let z1 = z0 * scaleRatio
        z1 = Math.min(ZOOM_STEP_RATIO_MAX, Math.max(ZOOM_STEP_RATIO_MIN, z1))
        const r = z1 / z0
        if (Math.abs(r - 1) < 1e-12) {
          return { ...l, zoomFit: false }
        }
        const panFx = (1 - r) * (viewportX - vw / 2) + r * l.panX
        const panFy = (1 - r) * (viewportY - vh / 2) + r * l.panY
        return {
          ...l,
          zoomFit: false,
          zoomLevel: z1,
          panX: panFx / r,
          panY: panFy / r,
        }
      })
    },
    [],
  )

  //......................................................................
  const zoomOut = useCallback(() => {
    setLayout(l => ({
      ...l,
      zoomFit: false,
      zoomLevel: Math.max(ZOOM_STEP_RATIO_MIN, l.zoomLevel * ZOOM_MOUSE_STEP),
    }))
  }, [])

  //......................................................................
  const zoomIn = useCallback(() => {
    setLayout(l => ({
      ...l,
      zoomFit: false,
      zoomLevel: Math.min(ZOOM_STEP_RATIO_MAX, l.zoomLevel / ZOOM_MOUSE_STEP),
    }))
  }, [])

  //......................................................................
  const zoomActual = useCallback(() => {
    setLayout(l => ({ ...l, zoomFit: false, zoomLevel: 1.0, panX: 0, panY: 0 }))
  }, [])

  //......................................................................
  const zoomFitMode = useCallback(() => {
    setLayout(l => ({ ...l, zoomFit: true, panX: 0, panY: 0 }))
  }, [])

  //......................................................................
  const applyExternalWidth = useCallback((width: number | undefined) => {
    setLayout(l => ({ ...l, externalWidth: width }))
  }, [])

  //......................................................................
  const scrubToIndex = useCallback((newIndex: number) => {
    setPlayback(p => ({
      ...p,
      currentIndex: newIndex,
      isPlaying: false,
    }))
  }, [])

  //......................................................................
  const applyPanOffset = useCallback((nextPanX: number, nextPanY: number) => {
    setLayout(l => ({ ...l, panX: nextPanX, panY: nextPanY }))
  }, [])

  //......................................................................
  const nudgePan = useCallback(
    (deltaX: number, deltaY: number) => {
      setLayout(l => {
        const next = clampPanToKeepViewerVisible(
          l.panX + deltaX,
          l.panY + deltaY,
          l.viewportSize,
          { width: viewerViewWidth, height: viewerViewHeight },
        )
        return { ...l, panX: next.panX, panY: next.panY }
      })
    },
    [viewerViewWidth, viewerViewHeight],
  )

  //......................................................................
  const chooseFrameRate = useCallback((fps: number) => {
    setPlayback(p => ({ ...p, frameRate: fps }))
  }, [])

  //......................................................................
  const chooseTimeSlot = useCallback((timeSlot: string | undefined) => {
    setFilters(f => {
      if (f.timeSlot === timeSlot) return f
      filterChangeRestoreRef.current = {
        current: photoToAnchor(currentPhoto) ?? null,
        in: inFrame >= 0 ? (photoToAnchor(viewerPhotos[inFrame]) ?? null) : null,
        out: outFrame >= 0 ? (photoToAnchor(viewerPhotos[outFrame]) ?? null) : null,
      }
      return { ...f, timeSlot }
    })
    setFeedState(s => ({ ...s, loadingStage: 0, currentPhoto: undefined }))
  }, [currentPhoto, inFrame, outFrame, viewerPhotos])

  //......................................................................
  const chooseMarkOutliers = useCallback((markOutliers: boolean) => {
    setFilters(f => {
      if (f.markOutliers === markOutliers) return f
      filterChangeRestoreRef.current = {
        current: photoToAnchor(currentPhoto) ?? null,
        in: inFrame >= 0 ? (photoToAnchor(viewerPhotos[inFrame]) ?? null) : null,
        out: outFrame >= 0 ? (photoToAnchor(viewerPhotos[outFrame]) ?? null) : null,
      }
      return { ...f, markOutliers }
    })
    setFeedState(s => ({ ...s, loadingStage: 0, currentPhoto: undefined }))
  }, [currentPhoto, inFrame, outFrame, viewerPhotos])

  //......................................................................
  const focusPlayerShell = useCallback(() => {
    playerShellRef.current?.focus()
  }, [])

  //......................................................................
  const fullScreen = useCallback(async (): Promise<void> => {
    const shell = playerShellRef.current
    if (!shell) return

    const doc = document as Document & {
      webkitFullscreenElement?: Element | null
      webkitExitFullscreen?: () => Promise<void> | void
    }
    const currentFs = doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null

    if (currentFs === shell) {
      if (doc.exitFullscreen) {
        await doc.exitFullscreen()
      } else {
        doc.webkitExitFullscreen?.()
      }
      return
    }

    if (currentFs) {
      if (doc.exitFullscreen) {
        await doc.exitFullscreen()
      } else {
        doc.webkitExitFullscreen?.()
      }
    }

    const shellWithWebkit = shell as HTMLDivElement & {
      webkitRequestFullscreen?: () => Promise<void> | void
    }
    if (shell.requestFullscreen) {
      await shell.requestFullscreen()
    } else {
      shellWithWebkit.webkitRequestFullscreen?.()
    }
  }, [])

  //......................................................................
  useEffect(() => {
    const doc = document as Document & {
      webkitFullscreenElement?: Element | null
    }
    const syncFullscreenState = () => {
      const fsEl = doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null
      setFullScreen(fsEl === playerShellRef.current)
    }

    syncFullscreenState()
    document.addEventListener('fullscreenchange', syncFullscreenState)
    document.addEventListener('webkitfullscreenchange', syncFullscreenState as EventListener)
    return () => {
      document.removeEventListener('fullscreenchange', syncFullscreenState)
      document.removeEventListener('webkitfullscreenchange', syncFullscreenState as EventListener)
    }
  }, [])

  //......................................................................
  const openPhotoInNewTab = useCallback((): void => {
    if (!currentPhoto || !cdnToken) {
      alert('No image to open')
      return
    }
    const url =
      `${cdnUrl}/${tenantId}/f/${feedId}/photo/${currentPhoto.s}`
      + `?tk=${encodeURIComponent(cdnToken)}`
    window.open(url, '_blank', 'noopener,noreferrer')
  }, [cdnUrl, tenantId, feedId, currentPhoto, cdnToken])

  //......................................................................
  const actions: AevocamPlayerActions = useMemo(
    () => ({
      reportSummaryLoaded: onSummaryLoaded,
      reportManifestLoaded: onManifestLoaded,
      reportCurrentPhoto: onCurrentPhoto,
      applyExternalWidth,
      setDirection,
      setPlaybackRepeat,
      setInFrame,
      setOutFrame,
      setStayBetweenInOut,
      advance,
      scrubToIndex,
      applyPanOffset,
      nudgePan,
      chooseFrameRate,
      play,
      stop,
      playPause,
      goFirst,
      goLast,
      bumpFrameRateUp,
      bumpFrameRateDown,
      zoomOut,
      zoomIn,
      zoomStepTowardViewportPoint,
      pinchZoomAtViewportPoint,
      zoomActual,
      zoomFit: zoomFitMode,
      setViewerLowSpriteGear: setSpritesHighGearLow,
      setViewerMediumSpriteGear: setSpritesHighGearMedium,
      chooseTimeSlot,
      chooseMarkOutliers,
      focusPlayerShell,
      fullScreen,
      openPhotoInNewTab,
    }),
    [
      onSummaryLoaded,
      onManifestLoaded,
      onCurrentPhoto,
      setSpritesHighGearLow,
      setSpritesHighGearMedium,
      applyExternalWidth,
      setDirection,
      setPlaybackRepeat,
      setInFrame,
      setOutFrame,
      setStayBetweenInOut,
      advance,
      scrubToIndex,
      applyPanOffset,
      nudgePan,
      chooseFrameRate,
      chooseTimeSlot,
      chooseMarkOutliers,
      play,
      stop,
      playPause,
      goFirst,
      goLast,
      bumpFrameRateUp,
      bumpFrameRateDown,
      zoomOut,
      zoomIn,
      zoomStepTowardViewportPoint,
      pinchZoomAtViewportPoint,
      zoomActual,
      zoomFitMode,
      focusPlayerShell,
      fullScreen,
      openPhotoInNewTab,
    ],
  )

  //......................................................................
  return {
    startAtLatest,
    sessionKey,
    cdnUrl,
    apiUrl,
    tenantId,
    feedId,
    keyboardHandlersGlobal,
    timeSlot: filters.timeSlot,
    markOutliers: filters.markOutliers,
    containerRef,
    skipNextViewportClickRef,
    deferPlayPauseRef,
    viewportRef,
    playerShellRef,
    viewportSizeRef,
    playbackIntervalRef,
    currentIndex,
    isPlaying,
    playReverse,
    playbackRepeat,
    inFrame,
    outFrame,
    stayBetweenInOut,
    frameRate,
    frameRateOptions,
    loadingStage,
    totalPhotos,
    feed,
    currentPhoto,
    rawContainerWidth,
    containerWidth,
    zoomLevel,
    zoomFit,
    panX,
    panY,
    proactiveLoading,
    viewportSize,
    externalWidth,
    isFullScreen: isFullScreen,
    viewerWidth,
    calculatedFitZoom,
    aspectRatio,
    viewerPipelineStage,
    cdnToken,
    cdnTokenTtlSec,
    viewerPhotos,
    spriteSheetsLow,
    spriteSheetsMedium,
    spritesHighGearLow,
    spritesHighGearMedium,
    viewerViewWidth,
    viewerViewHeight,
    actions,
    viewerFitsInViewport,
  }
}

export type PlaybackRepeatMode = 'single' | 'loop' | 'oscillate'

type PlaybackState = {
  currentIndex: number
  isPlaying: boolean
  playReverse: boolean
  frameRate: number
  frameRateOptions: number[]
  /** Stop at ends, wrap, or oscillate during timed playback. */
  playbackRepeat: PlaybackRepeatMode
  /** In point for ranged playback; `-1` = unset. */
  inFrame: number
  /** Out point for ranged playback; `-1` = unset. */
  outFrame: number
  /** When true and in/out form a valid range, playback and `advance` stay within that span. */
  stayBetweenInOut: boolean
}

type LayoutState = {
  rawContainerWidth: number | undefined
  externalWidth: number | undefined
  zoomLevel: number
  zoomFit: boolean
  panX: number
  panY: number
  proactiveLoading: boolean
  viewportSize: { width: number; height: number }
}

type FeedState = {
  loadingStage: number
  totalPhotos: number
  feed: Feed | null
  currentPhoto: Photo | undefined
}

type FilterState = {
  /** Optional hour filter (HHMMSS string, e.g. "130000"); undefined means all hours. */
  timeSlot: string | undefined
  /** When true, manifest fetch asks the API to flag statistical outliers. */
  markOutliers: boolean
}

//--------------------------------------------------------------------------------
export type AevocamPlayerActions = {
  /** Viewer fetched summary — updates chrome feed index, loading stage, etc. */
  reportSummaryLoaded: (feed: Feed, totalPhotos: number) => void
  reportManifestLoaded: () => void
  reportCurrentPhoto: (photo: Photo | undefined) => void

  /** Fixed width from host shell (e.g. `AevocamPlayer` width prop) */
  applyExternalWidth: (width: number | undefined) => void

  /** Timed playback direction and prefetch hint (`playReverse` in state). */
  setDirection: (reverse: boolean) => void
  setPlaybackRepeat: (mode: PlaybackRepeatMode) => void
  setInFrame: (index?: number) => void
  setOutFrame: (index?: number) => void
  setStayBetweenInOut: (value: boolean) => void

  /**
   * Step the current index by `frames` in absolute terms: positive toward higher indices,
   * negative toward lower. With `limitToInOut`, clamps to in/out when valid; default is full feed.
   */
  advance: (frames: number, limitToInOut?: boolean) => void
  /** Scrubber: jump to index and stop playback (does not change direction). */
  scrubToIndex: (newIndex: number) => void
  /** Pan offset in px (viewport-centered transform) */
  applyPanOffset: (panX: number, panY: number) => void
  /** Add delta pan in px, clamped like viewport drag. {@link AevocamPlayerShell} uses Shift+arrows only when `zoomFit` is off. */
  nudgePan: (deltaX: number, deltaY: number) => void
  chooseFrameRate: (fps: number) => void

  /**
   * Start timed playback. Pass `autoReverse: true` to flip direction when already at the
   * end of travel (honors stay-between-in/out bounds). Omit or false to keep `playReverse`.
   */
  play: (autoReverse?: boolean) => void
  stop: () => void
  /** Equivalent to toggling via `play(true)` or `stop()`. */
  playPause: (reverse: boolean) => void
  /** Jump to first index (`0`, or in-point when `limitToInOut` is true and in/out are valid). Default: full feed. */
  goFirst: (limitToInOut?: boolean) => void
  /** Jump to last index (or out-point when `limitToInOut` is true and in/out are valid). Default: full feed. */
  goLast: (limitToInOut?: boolean) => void

  bumpFrameRateUp: () => void
  bumpFrameRateDown: () => void

  zoomOut: () => void
  zoomIn: () => void
  /**
   * Exponential zoom step (same as +/-) keeping the point at `(viewportX, viewportY)`
   * fixed in the viewport; use viewport-local coords (e.g. `clientX - rect.left`).
   * `deltaY < 0` zooms in, `deltaY > 0` zooms out (wheel convention).
   */
  zoomStepTowardViewportPoint: (deltaY: number, viewportX: number, viewportY: number) => void
  /** Touch pinch: ratio of finger separation vs previous move event; viewport-local focal point. */
  pinchZoomAtViewportPoint: (scaleRatio: number, viewportX: number, viewportY: number) => void
  zoomActual: () => void
  zoomFit: () => void

  /** Prefetch layer toggles (wired from sprite prefetch) */
  setViewerLowSpriteGear: (value: boolean | ((prev: boolean) => boolean)) => void
  setViewerMediumSpriteGear: (value: boolean | ((prev: boolean) => boolean)) => void

  /** Set optional hour filter for manifest fetch. */
  chooseTimeSlot: (timeSlot: string | undefined) => void

  /** Toggle statistical outlier marking on manifest fetch and timeline. */
  chooseMarkOutliers: (markOutliers: boolean) => void

  /** Move keyboard focus to the outer {@link AevocamPlayerShell} (`tabIndex={0}`). */
  focusPlayerShell: () => void

  /** Toggle fullscreen for this player's shell (includes viewport + keyboard handlers). */
  fullScreen: () => Promise<void>

  /** Copy the current full-resolution photo to the system clipboard. */
  /** Open the current full-resolution photo URL in a new browser tab. */
  openPhotoInNewTab: () => void
}

//--------------------------------------------------------------------------------
export type AevocamPlayerContextValue = {
  startAtLatest: boolean
  sessionKey: string | undefined
  cdnUrl: string
  apiUrl: string
  tenantId: string
  feedId: string
  keyboardHandlersGlobal: boolean
  timeSlot: string | undefined
  markOutliers: boolean

  containerRef: RefObject<HTMLDivElement | null>
  /** Set by `<AevocamViewport>` after a real pan drag; consumed by click handlers to avoid toggling playback. */
  skipNextViewportClickRef: RefObject<boolean>
  /** When true, next viewport click skips play/pause (focus was outside the shell until that mousedown). */
  deferPlayPauseRef: RefObject<boolean>
  viewportRef: RefObject<HTMLDivElement | null>
  /** Outer {@link AevocamPlayerShell} node; prefer {@link AevocamPlayerActions.focusPlayerShell}. */
  playerShellRef: RefObject<HTMLDivElement | null>
  viewportSizeRef: RefObject<{ width: number; height: number }>
  playbackIntervalRef: RefObject<number | null>

  currentIndex: number
  isPlaying: boolean
  playReverse: boolean
  playbackRepeat: PlaybackRepeatMode
  inFrame: number
  outFrame: number
  stayBetweenInOut: boolean
  frameRate: number
  /** Discrete fps presets for the frame-rate control and keyboard nudging */
  frameRateOptions: readonly number[]
  loadingStage: number
  totalPhotos: number
  feed: Feed | null
  currentPhoto: Photo | undefined
  rawContainerWidth: number | undefined
  containerWidth: number | undefined
  zoomLevel: number
  zoomFit: boolean
  panX: number
  panY: number
  proactiveLoading: boolean
  viewportSize: { width: number; height: number }
  externalWidth: number | undefined
  isFullScreen: boolean

  viewerWidth: number | undefined
  calculatedFitZoom: number | undefined
  aspectRatio: number

  /** CDN / manifest pipeline (see `useAevocamAssetState`) */
  viewerPipelineStage: number
  cdnToken: string | undefined
  cdnTokenTtlSec: number
  viewerPhotos: (Photo | undefined)[]
  spriteSheetsLow: SpriteSheet[]
  spriteSheetsMedium: SpriteSheet[]
  spritesHighGearLow: boolean
  spritesHighGearMedium: boolean
  viewerViewWidth: number
  viewerViewHeight: number

  actions: AevocamPlayerActions
  viewerFitsInViewport: () => boolean
}

const AevocamPlayerContext = createContext<AevocamPlayerContextValue | null>(null)

//--------------------------------------------------------------------------------
/**
 * Inclusive [lo, hi] when `stayBetweenInOut` is true; otherwise full feed [0, cap].
 * With limit on: only in → [in, last]; only out → [0, out]; both → [in, out] (in ≤ out from UI).
 * Limit on with neither marker set → full feed (same as limit off for bounds).
 */
export function playbackIndexBounds(
  stayBetweenInOut: boolean,
  inFrame: number,
  outFrame: number,
  totalPhotos: number,
): { lo: number; hi: number } {
  const cap = Math.max(totalPhotos - 1, 0)
  if (!stayBetweenInOut) {
    return { lo: 0, hi: cap }
  }
  const hasIn = inFrame >= 0
  const hasOut = outFrame >= 0
  if (!hasIn && !hasOut) {
    return { lo: 0, hi: cap }
  }
  if (hasIn && hasOut) {
    const lo = Math.min(Math.max(inFrame, 0), cap)
    const hi = Math.min(Math.max(outFrame, lo), cap)
    return { lo, hi }
  }
  if (hasIn) {
    const lo = Math.min(Math.max(inFrame, 0), cap)
    return { lo, hi: cap }
  }
  const hi = Math.min(Math.max(outFrame, 0), cap)
  return { lo: 0, hi }
}

//--------------------------------------------------------------------------------

const INACTIVITY_MS = 60 * 1000

/** One step of exponential zoom (matches `zoomIn` / `zoomOut` and shift+wheel). */
const ZOOM_MOUSE_STEP = 0.9
const ZOOM_SHIFT_WHEEL_STEP = ZOOM_MOUSE_STEP * 1.07
const ZOOM_STEP_RATIO_MIN = 0.1
const ZOOM_STEP_RATIO_MAX = 20.0

/** Discrete frame-rate presets (fps), used for playback and keyboard nudging */
const DEFAULT_FRAME_RATE_OPTIONS: number[] = [
  0.5, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15, 20, 24, 30, 45, 60, 75, 90,
]
